import clsx from "clsx";
import { useMemo } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import SimpleDataTable from "common/components/SimpleDataTable";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import ProfileImage from "common/components/ui/ProfileImage";
import type { Member } from "common/types";

import { formatBillingDate } from "../../../utils";
import MemberStatusTag from "../MemberStatusTag";
import DesktopActionsDropdown from "./DesktopActionsDropdown";

import type { ColumnDef } from "@tanstack/react-table";

export default function DesktopTable({ members }: { members: Member[] }) {
  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        id: "name",
        header: "Member's name",
        cell: ({ row }) => {
          const {
            first_name: firstName = "New",
            last_name: lastName = "Member",
            role,
            customer_id: memberId,
            email,
            status,
          } = row.original;

          const isPending = status === "pending";

          return (
            <div className="flex items-center gap-2.5">
              {isPending ? (
                <ProfileImage src="" />
              ) : (
                <AimfoxAvatar firstName={firstName} lastName={lastName} />
              )}

              <div className="flex flex-col items-start gap-1">
                <span
                  className={clsx(
                    "text-md-bold",
                    isPending ? "text-black-400" : "text-black-950",
                  )}
                >
                  {memberId
                    ? `${firstName} ${lastName}`
                    : `New ${role === "member" ? "Member" : "Admin"}`}
                </span>

                <span
                  className={clsx(
                    "inline-block max-w-[40ch] overflow-hidden text-ellipsis text-xs-medium",
                    isPending ? "text-black-400" : "text-black-500",
                  )}
                >
                  {email}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        header: "Role",
        cell: ({ row }) => (
          <MemberPermissionsTag
            permissions={row.original.permissions}
            role={row.original.role}
          />
        ),
      },
      {
        id: "dateAdded",
        header: "Date Added",
        cell: ({ row }) => {
          const { role, status, created_at: createdAt } = row.original;
          const isPending = status === "pending";

          return (
            <span className={isPending ? "text-black-500" : "text-black-950"}>
              {role === "owner" ? "-" : formatBillingDate(createdAt)}
            </span>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <MemberStatusTag
            memberStatus={row.original.status}
            memberRole={row.original.role}
            size="md"
          />
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => <DesktopActionsDropdown member={row.original} />,
      },
    ],
    [],
  );

  return (
    <SimpleDataTable
      columns={columns}
      data={members}
      localStorageId="members"
    />
  );
}
